/* eslint-disable @typescript-eslint/no-explicit-any */
import { graphApiBaseUrl } from '../env-variables.config';

import { getItemType } from '../constants/utilityFunctions';
import { graphFetch } from '../api/graphClient';
import { useState } from 'react';
import { MailMetadata } from '../interface/mailMetadata';

export const useEmailCategoryTagger = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);

  const createCategory = async (
    categoryName: string,
    color: string, // e.g., "preset0", "preset1", ..., "preset25"
  ) => {
    const response = await graphFetch(`${graphApiBaseUrl}/outlook/masterCategories`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        displayName: categoryName,
        color,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create category');
    }
  };

  const ensureCategoryExists = async (categoryName: string, color: string) => {
    const existingCategoriesRes = await graphFetch(`${graphApiBaseUrl}/outlook/masterCategories`);
    const data = await existingCategoriesRes.json();

    const exists = data.value?.some(
      (c: { displayName: string }) => c.displayName.toLowerCase() === categoryName.toLowerCase(),
    );
    if (!exists) {
      await createCategory(categoryName, color);
    }
  };

  const assignCategoryToEmail = async (
    restItemId: string,
    categoryName: string,
  ): Promise<boolean> => {
    const itemType = getItemType();
    const messageUrl = `${graphApiBaseUrl}/${itemType}/${restItemId}`;

    // Step 1: Fetch existing categories
    const getResponse = await graphFetch(messageUrl);

    if (!getResponse.ok) {
      return false;
    }

    const messageData = await getResponse.json();
    const existingCategories: string[] = messageData.categories ?? [];

    // Step 2: Add the new category if not already present
    const updatedCategories = existingCategories.includes(categoryName)
      ? existingCategories
      : [...existingCategories, categoryName];

    const response = await graphFetch(messageUrl, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        categories: updatedCategories,
      }),
    });

    return response.ok;
  };
  const assignMetaDataToEmailViaGraph = async (
    restItemId: string,
    metaData: Record<string, string | null>, // dynamic metadata
  ) => {
    const itemType = getItemType();
    const extensionUrl = `${graphApiBaseUrl}/${itemType}/${restItemId}/extensions/com.contoso.csiExtraData`;
    const extensionData = {
      '@odata.type': 'microsoft.graph.openTypeExtension',
      extensionName: 'com.contoso.csiExtraData',
      createdBy: Office.context.mailbox.userProfile.emailAddress,
      timestamp: new Date().toISOString(),
      ...metaData,
    };

    // Try to POST first
    const createRes = await graphFetch(`${graphApiBaseUrl}/${itemType}/${restItemId}/extensions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(extensionData),
    });

    // If already exists, PATCH instead
    if (!createRes.ok) {
      const patchRes = await graphFetch(extensionUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...metaData,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!patchRes.ok) {
        throw new Error('Failed to update existing metadata extension.');
      }
    }
  };
  const getEmailCategories = async (restId: string) => {
    const itemType = getItemType();
    const res = await graphFetch(`${graphApiBaseUrl}/${itemType}/${restId}`);
    const json = await res.json();
    return (json.categories as string[]) ?? [];
  };

  const removeSpecificCategory = async (restId: string, categoryToRemove: string) => {
    const itemType = getItemType();
    const categories = await getEmailCategories(restId);
    const filtered = categories.filter((cat) => cat !== categoryToRemove);
    await graphFetch(`${graphApiBaseUrl}/${itemType}/${restId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ categories: filtered }),
    });
  };
  const getEmailMetadataResponse = async (restItemId: string): Promise<unknown> => {
    const itemType = getItemType();

    const response = await graphFetch(
      `${graphApiBaseUrl}/${itemType}/${restItemId}?$expand=extensions($filter=id eq 'com.contoso.csiExtraData')`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) {
      return null;
    }

    return await response.json();
  };

  const getCustomExtensionFromMetadata = (data: unknown): MailMetadata | null => {
    const extensions = (data as any)?.extensions ?? [];
    const customExtension = extensions.find(
      (ext: { id: string }) =>
        ext.id === 'Microsoft.OutlookServices.OpenTypeExtension.com.contoso.csiExtraData',
    );
    return customExtension ?? null;
  };

  const getCustomMetadataFromEmail = async (
    restItemId: string,
  ): Promise<{ itemDetails: unknown; customExtension: MailMetadata | null }> => {
    const itemDetails = await getEmailMetadataResponse(restItemId);
    const customExtension = getCustomExtensionFromMetadata(itemDetails);
    return { itemDetails, customExtension };
  };
  const getItemId = (
    item:
      | (Office.ItemCompose &
          Office.ItemRead &
          Office.Message &
          Office.MessageCompose &
          Office.MessageRead &
          Office.Appointment &
          Office.AppointmentCompose &
          Office.AppointmentRead)
      | undefined,
  ): Promise<string | null> => {
    return new Promise<string | null>((resolve) => {
      item?.getItemIdAsync((result: Office.AsyncResult<string>) => {
        if (result.status === Office.AsyncResultStatus.Succeeded && result.value) {
          resolve(result.value);
        } else {
          resolve(null);
        }
      });
    });
  };

  const tagEmailWithCategory = async (categoryName: string, metaData: Record<string, string>) => {
    setLoading(true);
    setError(null);

    try {
      const item = Office.context.mailbox.item;
      const assignWithRestId = async (itemId: string) => {
        const restId = Office.context.mailbox.convertToRestId(
          itemId,
          Office.MailboxEnums.RestVersion.v2_0,
        );
        // Wait for Outlook/Graph to sync the item (1-2 seconds)
        await new Promise((res) => setTimeout(res, 1500));
        await ensureCategoryExists(categoryName, 'Preset20');
        // Wait for Outlook/Graph to register the new category (1-2 seconds)
        await new Promise((res) => setTimeout(res, 1500));
        await assignCategoryToEmail(restId, categoryName);
        await assignMetaDataToEmailViaGraph(restId, metaData);
      };

      if (item) {
        if (typeof item.getItemIdAsync === 'function') {
          await new Promise<Office.AsyncResult<string>>((resolve, reject) => {
            item.saveAsync((saveResult) => {
              if (saveResult.status === Office.AsyncResultStatus.Succeeded) {
                resolve(saveResult);
              } else {
                reject(new Error('Failed to save draft email'));
              }
            });
          });

          // Promisify getItemIdAsync
          const itemId = await new Promise<string | null>((resolve) => {
            item.getItemIdAsync((idResult) => {
              if (idResult.status === Office.AsyncResultStatus.Succeeded && idResult.value) {
                resolve(idResult.value);
              } else {
                resolve(null);
              }
            });
          });

          if (itemId) {
            await assignWithRestId(itemId);
          }
        } else if (item.itemId) {
          await assignWithRestId(item.itemId);
        } else {
          setError('Unable to retrieve item ID');
        }
      } else {
        setError('No active item in context');
      }
    } catch (err: unknown) {
      console.error('Error tagging email:', err);
      setError((err as { message: string }).message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };
  const untagEmailCategory = async (
    category: string | RegExp,
    metaData: Record<string, string | null>,
  ) => {
    setLoading(true);
    setError(null);

    try {
      const item = Office.context.mailbox.item;

      const processItem = async (itemId: string) => {
        const restId = Office.context.mailbox.convertToRestId(
          itemId,
          Office.MailboxEnums.RestVersion.v2_0,
        );
        let categoriesToRemove: string[] = [];

        if (typeof category === 'string') {
          categoriesToRemove = [category];
        } else if (category instanceof RegExp) {
          const allCategories = await getEmailCategories(restId);
          categoriesToRemove = allCategories.filter((cat) => category.test(cat));
        }

        for (const cat of categoriesToRemove) {
          await removeSpecificCategory(restId, cat);
          await new Promise((res) => setTimeout(res, 500));
        }
        await assignMetaDataToEmailViaGraph(restId, metaData);
      };

      if (item) {
        if (typeof item.getItemIdAsync === 'function') {
          await new Promise<Office.AsyncResult<string>>((resolve, reject) => {
            item.saveAsync((saveResult) => {
              if (saveResult.status === Office.AsyncResultStatus.Succeeded) {
                resolve(saveResult);
              } else {
                reject(new Error('Failed to save draft email'));
              }
            });
          });

          const itemId = await new Promise<string | null>((resolve) => {
            item.getItemIdAsync((idResult) => {
              if (idResult.status === Office.AsyncResultStatus.Succeeded && idResult.value) {
                resolve(idResult.value);
              } else {
                resolve(null);
              }
            });
          });

          if (itemId) {
            await processItem(itemId);
          }
        } else if (item.itemId) {
          await processItem(item.itemId);
        } else {
          setError('Unable to retrieve item ID');
        }
      } else {
        setError('No active item in context');
      }
    } catch (err: unknown) {
      console.error('Error untagging email:', err);
      setError((err as { message: string }).message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };
  const getFolderNameForItem = async (restId: string) => {
    const itemType = getItemType();
    const response = await graphFetch(`${graphApiBaseUrl}/${itemType}/${restId}`);
    const message = await response.json();
    const folderId = message.parentFolderId;
    // Fetch the folder details
    const folderRes = await graphFetch(`${graphApiBaseUrl}/mailFolders/${folderId}`);
    const folder = await folderRes.json();
    return folder.displayName;
  };
  return {
    untagEmailCategory,
    getCustomMetadataFromEmail,
    getEmailMetadataResponse,
    tagEmailWithCategory,
    getFolderNameForItem,
    getItemId,
    loading,
    error,
  };
};
